import React, { useEffect, useState } from "react";
import { Mail, Users, Send, Trash2, Plus, Paperclip, PlayCircle, AlertTriangle } from "lucide-react";
import { api } from "@/lib/apiClient";
import { PageHeader, Loader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

function StatCard({ icon: Icon, value, label, testId }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-5 shadow-sm flex items-center gap-4" data-testid={testId}>
      <span className="w-12 h-12 rounded-2xl bg-teal/10 text-teal flex items-center justify-center"><Icon className="w-6 h-6" /></span>
      <div><p className="font-heading text-2xl font-bold text-navy leading-none">{value}</p><p className="text-sm text-muted-foreground mt-1">{label}</p></div>
    </div>
  );
}

// Reusable attachment uploader -> returns {file_id, filename}
function AttachmentField({ value, onChange, testId }) {
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/admin/marketing/attachment", fd);
      onChange({ attachment_id: data.file_id, attachment_name: data.filename });
    } catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3 flex-wrap">
      {value?.attachment_name ? <span className="text-xs text-teal truncate max-w-[220px]"><Paperclip className="w-3 h-3 inline mr-1" />{value.attachment_name}</span> : null}
      <label className="border border-dashed border-border rounded-xl px-3 py-2 text-xs text-muted-foreground cursor-pointer hover:border-teal transition-colors" data-testid={testId}>
        <Paperclip className="w-3.5 h-3.5 inline mr-1" />{busy ? "Uploading..." : "Attach QR / file"}
        <input type="file" className="hidden" onChange={up} />
      </label>
      {value?.attachment_id ? <button type="button" className="text-xs text-red-500" onClick={() => onChange({ attachment_id: null, attachment_name: null })}>Remove</button> : null}
    </div>
  );
}

function ContactsTab({ contacts, reload, sequences, onEnroll }) {
  const [email, setEmail] = useState(""); const [name, setName] = useState("");
  const [bulk, setBulk] = useState("");
  const [selected, setSelected] = useState([]);
  const [seqId, setSeqId] = useState("");
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const addOne = async () => {
    if (!email.trim()) return toast.error("Enter an email");
    const { data } = await api.post("/admin/marketing/contacts", { email, name });
    toast.success(`${data.added} contact added`); setEmail(""); setName(""); reload();
  };
  const addBulk = async () => {
    if (!bulk.trim()) return toast.error("Paste some emails");
    const { data } = await api.post("/admin/marketing/contacts", { emails: bulk });
    toast.success(`${data.added} added (${data.total_parsed} found)`); setBulk(""); reload();
  };
  const del = async (id) => { await api.delete(`/admin/marketing/contacts/${id}`); reload(); };
  const enroll = async () => {
    if (!seqId) return toast.error("Pick a sequence");
    if (!selected.length) return toast.error("Select contacts first");
    await onEnroll(seqId, selected); setSelected([]);
  };
  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-border rounded-2xl p-5 shadow-sm space-y-3">
          <p className="font-heading font-semibold">Add a single contact</p>
          <div><Label>Email</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@example.com" data-testid="mk-contact-email" /></div>
          <div><Label>Name (optional)</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="mk-contact-name" /></div>
          <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={addOne} data-testid="mk-contact-add"><Plus className="w-4 h-4 mr-1.5" />Add Contact</Button>
        </div>
        <div className="bg-white border border-border rounded-2xl p-5 shadow-sm space-y-3">
          <p className="font-heading font-semibold">Bulk add</p>
          <Textarea rows={4} value={bulk} onChange={(e) => setBulk(e.target.value)} placeholder="Paste emails separated by commas, spaces or new lines" data-testid="mk-bulk-text" />
          <Button variant="outline" className="rounded-full" onClick={addBulk} data-testid="mk-bulk-add"><Plus className="w-4 h-4 mr-1.5" />Add List</Button>
        </div>
      </div>

      <div className="bg-white border border-border rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <p className="font-heading font-semibold">{contacts.length} contacts</p>
          {selected.length > 0 && (
            <div className="flex items-center gap-2">
              <select value={seqId} onChange={(e) => setSeqId(e.target.value)} className="border border-border rounded-full px-3 py-1.5 text-sm" data-testid="mk-enroll-seq">
                <option value="">Enroll {selected.length} into…</option>
                {sequences.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <Button size="sm" className="rounded-full bg-teal hover:bg-teal/90" onClick={enroll} data-testid="mk-enroll-btn">Enroll</Button>
            </div>
          )}
        </div>
        {contacts.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">No contacts yet.</p> : (
          <div className="divide-y divide-border">
            {contacts.map((c) => (
              <div key={c.id} className="flex items-center gap-3 py-2.5" data-testid={`mk-contact-${c.id}`}>
                <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} className="accent-teal w-4 h-4" data-testid={`mk-select-${c.id}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{c.email}</p>
                  {c.name && <p className="text-xs text-muted-foreground">{c.name}</p>}
                </div>
                <span className="text-[11px] text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{c.source}</span>
                <button onClick={() => del(c.id)} className="text-red-500" data-testid={`mk-del-${c.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SequenceEditor({ open, onClose, initial, onSaved }) {
  const blank = { name: "", active: true, steps: [{ subject: "", body: "", delay_days: 0, attachment_id: null, attachment_name: null }] };
  const [seq, setSeq] = useState(blank);
  useEffect(() => { setSeq(initial ? JSON.parse(JSON.stringify(initial)) : blank); }, [initial, open]);
  const setStep = (i, patch) => setSeq((s) => ({ ...s, steps: s.steps.map((st, idx) => idx === i ? { ...st, ...patch } : st) }));
  const addStep = () => setSeq((s) => ({ ...s, steps: [...s.steps, { subject: "", body: "", delay_days: 3, attachment_id: null, attachment_name: null }] }));
  const removeStep = (i) => setSeq((s) => ({ ...s, steps: s.steps.filter((_, idx) => idx !== i) }));
  const save = async () => {
    if (!seq.name.trim()) return toast.error("Name your sequence");
    if (initial?.id) await api.put(`/admin/marketing/sequences/${initial.id}`, seq);
    else await api.post("/admin/marketing/sequences", seq);
    toast.success("Sequence saved"); onSaved();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="mk-seq-editor">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit sequence" : "New sequence"}</DialogTitle><DialogDescription>Step 1 sends immediately on enrollment; later steps send after their day delay.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><Label>Sequence name</Label><Input value={seq.name} onChange={(e) => setSeq({ ...seq, name: e.target.value })} placeholder="e.g. New lead welcome" data-testid="mk-seq-name" /></div>
          {seq.steps.map((st, i) => (
            <div key={i} className="border border-border rounded-xl p-4 space-y-3 bg-muted/30" data-testid={`mk-step-${i}`}>
              <div className="flex items-center justify-between">
                <p className="font-heading font-semibold text-sm">Step {i + 1}</p>
                {seq.steps.length > 1 && <button onClick={() => removeStep(i)} className="text-red-500 text-xs" data-testid={`mk-step-del-${i}`}><Trash2 className="w-3.5 h-3.5 inline" /> Remove</button>}
              </div>
              <div><Label>Subject</Label><Input value={st.subject} onChange={(e) => setStep(i, { subject: e.target.value })} data-testid={`mk-step-subject-${i}`} /></div>
              <div><Label>Body</Label><Textarea rows={4} value={st.body} onChange={(e) => setStep(i, { body: e.target.value })} data-testid={`mk-step-body-${i}`} /></div>
              <div>
                <Label>{i === 0 ? "Send timing" : "Send delay (days after previous step)"}</Label>
                {i === 0 ? <p className="text-xs text-muted-foreground mt-1">Sent immediately when a contact is enrolled.</p>
                  : <Input type="number" min="0" value={st.delay_days} onChange={(e) => setStep(i, { delay_days: parseInt(e.target.value) || 0 })} className="w-28" data-testid={`mk-step-delay-${i}`} />}
              </div>
              <AttachmentField value={st} onChange={(patch) => setStep(i, patch)} testId={`mk-step-attach-${i}`} />
            </div>
          ))}
          <Button variant="outline" className="rounded-full" onClick={addStep} data-testid="mk-add-step"><Plus className="w-4 h-4 mr-1.5" />Add Step</Button>
        </div>
        <DialogFooter><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="mk-seq-save">Save Sequence</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SequencesTab({ sequences, reload, contacts, onEnrollAll }) {
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const newSeq = () => { setEditing(null); setOpen(true); };
  const edit = (s) => { setEditing(s); setOpen(true); };
  const del = async (id) => { await api.delete(`/admin/marketing/sequences/${id}`); reload(); };
  const toggleActive = async (s) => { await api.put(`/admin/marketing/sequences/${s.id}`, { active: !s.active }); reload(); };
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">Create multi-step drip flows. Step 1 sends on enrollment; later steps send after their day delay.</p>
        <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={newSeq} data-testid="mk-new-sequence"><Plus className="w-4 h-4 mr-1.5" />New Sequence</Button>
      </div>
      {sequences.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center bg-white border border-border rounded-2xl">No sequences yet.</p> : (
        <div className="grid md:grid-cols-2 gap-4">
          {sequences.map((s) => (
            <div key={s.id} className="bg-white border border-border rounded-2xl p-5 shadow-sm" data-testid={`mk-seq-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div><p className="font-heading font-bold">{s.name}</p><p className="text-xs text-muted-foreground">{s.steps?.length || 0} step{(s.steps?.length || 0) === 1 ? "" : "s"}</p></div>
                <div className="flex items-center gap-2"><span className="text-xs text-muted-foreground">Active</span><Switch checked={s.active !== false} onCheckedChange={() => toggleActive(s)} data-testid={`mk-seq-active-${s.id}`} /></div>
              </div>
              <div className="flex gap-2 mt-4 flex-wrap">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => edit(s)} data-testid={`mk-seq-edit-${s.id}`}>Edit</Button>
                <Button size="sm" className="rounded-full bg-teal hover:bg-teal/90" onClick={() => onEnrollAll(s.id)} data-testid={`mk-seq-enrollall-${s.id}`} disabled={!contacts.length}>Enroll all contacts</Button>
                <Button size="sm" variant="ghost" className="rounded-full text-red-500" onClick={() => del(s.id)} data-testid={`mk-seq-del-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <SequenceEditor open={open} onClose={() => setOpen(false)} initial={editing} onSaved={() => { setOpen(false); reload(); }} />
    </div>
  );
}

function SendNowTab({ contacts }) {
  const [subject, setSubject] = useState(""); const [body, setBody] = useState("");
  const [att, setAtt] = useState({ attachment_id: null, attachment_name: null });
  const [mode, setMode] = useState("all"); // all | selected | extra
  const [selected, setSelected] = useState([]);
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const send = async () => {
    if (!subject.trim() || !body.trim()) return toast.error("Subject and body required");
    const payload = { subject, body, attachment_id: att.attachment_id };
    if (mode === "all") payload.all = true;
    else if (mode === "selected") payload.contact_ids = selected;
    else { payload.contact_ids = []; payload.extra_emails = extra; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/marketing/send-now", payload);
      toast.success(`Sent ${data.sent} of ${data.total}`);
      setSubject(""); setBody(""); setAtt({ attachment_id: null, attachment_name: null }); setExtra(""); setSelected([]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-border rounded-2xl p-5 shadow-sm space-y-4 max-w-2xl">
      <p className="text-sm text-muted-foreground">Send a one-off email right now — for urgent announcements or last-minute session details.</p>
      <div><Label>Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="mk-send-subject" /></div>
      <div><Label>Body</Label><Textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)} data-testid="mk-send-body" /></div>
      <div><Label>Attachment (optional)</Label><div className="mt-1"><AttachmentField value={att} onChange={setAtt} testId="mk-send-attach" /></div></div>
      <div>
        <Label>Recipients</Label>
        <div className="flex gap-2 mt-1 flex-wrap">
          {["all", "selected", "extra"].map((m) => (
            <Button key={m} type="button" size="sm" variant={mode === m ? "default" : "outline"} className={`rounded-full ${mode === m ? "bg-teal hover:bg-teal/90" : ""}`} onClick={() => setMode(m)} data-testid={`mk-send-mode-${m}`}>
              {m === "all" ? "All contacts" : m === "selected" ? "Pick contacts" : "Type emails"}
            </Button>
          ))}
        </div>
      </div>
      {mode === "selected" && (
        <div className="border border-border rounded-xl p-3 max-h-48 overflow-y-auto divide-y divide-border">
          {contacts.map((c) => (
            <label key={c.id} className="flex items-center gap-2 py-1.5 text-sm cursor-pointer">
              <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} className="accent-teal w-4 h-4" data-testid={`mk-send-pick-${c.id}`} />
              {c.email}
            </label>
          ))}
          {!contacts.length && <p className="text-sm text-muted-foreground py-2">No contacts.</p>}
        </div>
      )}
      {mode === "extra" && <Textarea rows={3} value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="Emails separated by comma / space / new line" data-testid="mk-send-extra" />}
      <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={send} disabled={busy} data-testid="mk-send-now"><Send className="w-4 h-4 mr-1.5" />{busy ? "Sending..." : "Send Now"}</Button>
    </div>
  );
}

export default function AdminMarketing() {
  const [overview, setOverview] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [sequences, setSequences] = useState([]);
  const loadOverview = () => api.get("/admin/marketing/overview").then((r) => setOverview(r.data));
  const loadContacts = () => api.get("/admin/marketing/contacts").then((r) => setContacts(r.data));
  const loadSequences = () => api.get("/admin/marketing/sequences").then((r) => setSequences(r.data));
  useEffect(() => { loadOverview(); loadContacts(); loadSequences(); }, []);
  const enroll = async (seqId, ids) => {
    const { data } = await api.post(`/admin/marketing/sequences/${seqId}/enroll`, { contact_ids: ids });
    toast.success(`Enrolled ${data.enrolled} contact(s)`); loadOverview();
  };
  const enrollAll = async (seqId) => {
    const { data } = await api.post(`/admin/marketing/sequences/${seqId}/enroll`, { all: true });
    toast.success(`Enrolled ${data.enrolled} contact(s)`); loadOverview();
  };
  if (!overview) return <Loader />;
  return (
    <div>
      <PageHeader title="Email Marketing" subtitle="Contacts, drip sequences and one-off sends" />
      {!overview.sendgrid_ready && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-2xl p-4 mb-5 flex items-start gap-3" data-testid="mk-sendgrid-warning">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <p className="text-sm">SendGrid isn't connected yet — add a valid API key &amp; verified sender so emails can actually go out. You can still build contacts and sequences now.</p>
        </div>
      )}
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <StatCard icon={Users} value={overview.total_contacts} label="Total contacts" testId="mk-stat-contacts" />
        <StatCard icon={Mail} value={overview.total_sent} label="Emails sent" testId="mk-stat-sent" />
        <StatCard icon={PlayCircle} value={overview.total_bookings} label="Bookings received" testId="mk-stat-bookings" />
      </div>
      <Tabs defaultValue="contacts">
        <TabsList className="mb-6"><TabsTrigger value="contacts" data-testid="mk-tab-contacts">Contacts</TabsTrigger><TabsTrigger value="sequences" data-testid="mk-tab-sequences">Sequences</TabsTrigger><TabsTrigger value="send" data-testid="mk-tab-send">Send Now</TabsTrigger></TabsList>
        <TabsContent value="contacts"><ContactsTab contacts={contacts} reload={() => { loadContacts(); loadOverview(); }} sequences={sequences} onEnroll={enroll} /></TabsContent>
        <TabsContent value="sequences"><SequencesTab sequences={sequences} reload={loadSequences} contacts={contacts} onEnrollAll={enrollAll} /></TabsContent>
        <TabsContent value="send"><SendNowTab contacts={contacts} /></TabsContent>
      </Tabs>
    </div>
  );
}
